import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Copy, Check, Send } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import MessagingSwitches from "../../components/admin/MessagingSwitches";
import WhatsAppIcon from "../../components/ui/WhatsAppIcon";
import api from "../../api";

// NL Labs: one Meta-approved WhatsApp template per message flow (backend
// notifications/whatsapp_templates.py, table whatsapp_templates). Each card
// shows exactly what to enter in WhatsApp Manager; a flow sends only once it's
// switched on here (and WhatsApp is on at the top).
const STATUS = {
  ready: { label: "Sending", tone: "bg-emerald-100 text-emerald-800" },
  disabled: { label: "Off", tone: "bg-secondary-100 text-secondary-700" },
  channel_off: { label: "WhatsApp off", tone: "bg-secondary-100 text-secondary-700" },
  no_gateway: { label: "WhatsApp not set up", tone: "bg-amber-100 text-amber-800" },
};
const AUDIENCE = { patient: "To patients", staff: "To staff", clinic: "To the clinic" };
const inputCls = "w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm text-secondary-900";

function CopyButton({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* text stays selectable */ }
  };
  return (
    <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline">
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {label}
    </button>
  );
}

function SpecRow({ label, value, copy = true }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2 items-start">
      <span className="text-xs font-medium text-secondary-500 pt-0.5">{label}</span>
      <div className="min-w-0">
        <p className="text-sm text-secondary-900 whitespace-pre-wrap break-words">{value}</p>
        {copy && <CopyButton text={value} />}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block normal-case font-sans tracking-normal">
      <span className="text-sm font-medium text-secondary-800">{label}</span>
      {hint && <span className="block text-xs text-secondary-500">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function TemplateCard({ t, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ template_name: "", language: "en", notes: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [phone, setPhone] = useState("");

  useEffect(() => {
    setForm({ template_name: t.template_name || "", language: t.language || "en", notes: t.notes || "" });
  }, [t]);

  const save = async (data, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.saveWhatsAppTemplate(t.flow_key, data);
      onSaved(r.data);
      setMsg({ ok: true, text: okText });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.error || "Couldn't save." });
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.testWhatsAppTemplate(t.flow_key, phone);
      setMsg({ ok: true, text: "Test sent with the sample values." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.error || "Couldn't send the test." });
    } finally {
      setBusy(false);
    }
  };

  const status = STATUS[t.status] || STATUS.disabled;
  const auth = t.category === "authentication";
  return (
    <div className="rounded-xl border border-secondary-200 bg-white">
      <div className="flex flex-wrap items-center gap-3 p-4">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-[12rem] text-left">
          <p className="font-semibold text-secondary-900 flex items-center gap-2">
            {t.label} {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </p>
          <p className="text-xs text-secondary-500 mt-0.5">
            {AUDIENCE[t.audience]} · {t.template_name} · {t.language} · {auth ? "Authentication" : "Utility"}
          </p>
        </button>
        {!t.live && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Not sent by the app yet</span>}
        {t.throttled_today > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800" title="Blocked by the sending limits today.">
            {t.throttled_today} held back today
          </span>
        )}
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.tone}`}>{status.label}</span>
        <label className="inline-flex items-center gap-2 normal-case font-sans tracking-normal text-sm text-secondary-700 cursor-pointer">
          <input type="checkbox" className="h-4 w-4" checked={t.enabled} disabled={busy}
            onChange={(e) => save({ enabled: e.target.checked }, e.target.checked ? "Switched on." : "Switched off.")} />
          On
        </label>
      </div>
      {msg && !open && <p className={`px-4 pb-3 text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p>}

      {open && (
        <div className="border-t border-secondary-200 p-4 space-y-4">
          <div className="rounded-lg bg-secondary-50 p-3 space-y-3">
            <p className="text-sm font-medium text-secondary-800">To enter in WhatsApp Manager</p>
            <SpecRow label="Category" value={auth ? "Authentication" : "Utility"} copy={false} />
            <SpecRow label="Name" value={t.suggested_name} />
            <SpecRow label="Language" value={t.language === "en_US" ? "English (US)" : t.language === "en" ? "English" : t.language} copy={false} />
            <SpecRow label="Variable type" value={auth ? "Set by Meta's template" : "Name"} copy={false} />
            <SpecRow label="Header" value={t.header} />
            <SpecRow label="Body" value={t.body} />
            <SpecRow label="Footer" value={t.footer} />
            {t.buttons.map((b, i) => (
              <SpecRow key={i} label={`Button ${i + 1}`} copy={false}
                value={b.copy_code ? "Copy code" : `Visit website · "${b.text}" · ${b.dynamic ? "Dynamic" : "Static"} URL ${b.url}${b.sample ? `\nSample: ${b.sample}` : ""}`} />
            ))}
            {t.variables.length > 0 && (
              <div className="grid grid-cols-[7rem_1fr] gap-2">
                <span className="text-xs font-medium text-secondary-500 pt-0.5">Samples</span>
                <ul className="text-sm text-secondary-900 space-y-0.5">
                  {t.variables.map((v) => <li key={v.name}><code>{`{{${v.name}}}`}</code> {v.label}: {v.sample}</li>)}
                </ul>
              </div>
            )}
            {t.notes_for_meta && <p className="text-xs text-secondary-600">{t.notes_for_meta}</p>}
            <p className="text-xs text-secondary-500">Preview: {t.preview}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Template name" hint="Exactly as approved in WhatsApp Manager">
              <input className={inputCls} value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })} />
            </Field>
            <Field label="Language code" hint="Meta's code, exactly as the template's language: en_US for English (US), en for English">
              <input className={inputCls} value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} />
            </Field>
          </div>
          <Field label="Notes">
            <textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. submitted 1 Nov, approved as Utility 1 Nov" />
          </Field>

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" disabled={busy} onClick={() => save(form, "Saved.")}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-50">
              Save
            </button>
            <div className="flex items-center gap-2">
              <input className="rounded-lg border border-secondary-300 px-3 py-2 text-sm w-40" placeholder="Mobile number"
                value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" />
              <button type="button" disabled={busy || !phone} onClick={test}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-secondary-300 text-sm text-secondary-800 disabled:opacity-50">
                <Send className="w-4 h-4" /> Send test
              </button>
            </div>
          </div>
          {msg && <p className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p>}
        </div>
      )}
    </div>
  );
}

export default function AdminWhatsAppTemplates() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.fetchWhatsAppTemplates()
    .then((r) => setData(r.data))
    .catch((e) => setError(e?.response?.data?.error || "Couldn't load WhatsApp templates."));

  useEffect(() => { load(); }, []);

  const onSaved = (row) => setData((d) => ({ ...d, templates: d.templates.map((t) => (t.flow_key === row.flow_key ? row : t)) }));

  return (
    <DashboardLayout>
      <div className="container mx-auto p-4 md:p-8 max-w-5xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <WhatsAppIcon size={30} brand /> WhatsApp Templates
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Every WhatsApp message the platform starts, one approved template each. A message goes out only when it's switched on
            here, and for patient and crisis alerts, when the clinic has WhatsApp allowed too. Patients get alerts only after
            opting in (at sign-in or in My profile); anyone who replies STOP is never messaged again until they reply START.
          </p>
        </div>

        {error && <p className="text-red-600">{error}</p>}
        {!data && !error && <p className="text-secondary-500">Loading…</p>}
        {data && <MessagingSwitches channel="whatsapp" switches={data.switches} onChange={() => load()} />}
        {data && !data.gateway_ready && (
          <p className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            WhatsApp isn't set up on this server (WHATSAPP_PHONE_NUMBER_ID and WHATSAPP_ACCESS_TOKEN), so nothing will send yet.
            You can still save names and switches.
          </p>
        )}
        {data && !data.webhook_ready && (
          <p className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            The webhook isn't set up (WHATSAPP_APP_SECRET and WHATSAPP_VERIFY_TOKEN), so STOP replies, delivery receipts and the
            safety auto-reply won't work. Webhook URL: https://api.mindconnect.cloud/api/whatsapp/webhook
          </p>
        )}

        {data && data.groups.map((g) => {
          const rows = data.templates.filter((t) => t.group === g.key);
          if (!rows.length) return null;
          return (
            <section key={g.key} className="space-y-3">
              <h2 className="text-lg font-semibold text-secondary-900">{g.label}</h2>
              {rows.map((t) => <TemplateCard key={t.flow_key} t={t} onSaved={onSaved} />)}
            </section>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
